// ABOUTME: Collapsible sidebar - header, portfolio selector, nav groups, and bottom menu
// ABOUTME: Nav data lives in sidebar-nav.tsx; icons in sidebar-icons.tsx

import type { BrowserAuth } from '@boundarylab/auth/browser';
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { auth as defaultAuth } from '../lib/auth-client';
import { safeGetJson, safeSetJson } from '../lib/storage';
import { useModuleNavGroup } from './module-nav';
import { PortfolioSelector } from './PortfolioSelector';
import { SidebarUserMenu } from './SidebarUserMenu';
import { AlphaFinderIcon, ChevronDownIcon, ChevronLeftIcon } from './sidebar-icons';
import { isItemActive, navLinkClass } from './sidebar-link';
import { navGroups } from './sidebar-nav';

const COLLAPSED_GROUPS_KEY = 'sidebar_collapsed_groups';

function loadCollapsedGroups(): Set<string> {
  return new Set(safeGetJson<string[]>(COLLAPSED_GROUPS_KEY) ?? []);
}

function saveCollapsedGroups(groups: Set<string>) {
  safeSetJson(COLLAPSED_GROUPS_KEY, [...groups]);
}

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  auth?: BrowserAuth;
}

export function Sidebar({ isCollapsed, onToggle, auth = defaultAuth }: SidebarProps) {
  const { pathname } = useLocation();
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(loadCollapsedGroups);
  // Registered platform modules render as a "Modules" group after the built-in nav.
  const moduleGroup = useModuleNavGroup();
  const groups = moduleGroup ? [...navGroups, moduleGroup] : navGroups;

  function toggleGroup(title: string) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      saveCollapsedGroups(next);
      return next;
    });
  }

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-black border-r border-neutral-800 transition-all duration-300 z-40 flex flex-col ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="h-16 flex items-center justify-between px-4 border-b border-neutral-800">
        {isCollapsed ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center justify-center w-full"
            aria-label="Expand sidebar"
          >
            <AlphaFinderIcon className="w-6 h-6 text-white" />
          </button>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <AlphaFinderIcon className="w-6 h-6 text-white flex-shrink-0" />
              <span className="text-lg font-bold text-white">Alpha Finder</span>
            </div>
            <button
              type="button"
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-neutral-900 text-neutral-400 hover:text-white transition-colors"
              aria-label="Collapse sidebar"
            >
              <ChevronLeftIcon />
            </button>
          </>
        )}
      </div>

      <div className="border-b border-neutral-800">
        <PortfolioSelector isCollapsed={isCollapsed} />
      </div>

      <nav
        className={`p-3 overflow-y-auto flex-1 min-h-0 sidebar-nav-scroll ${isCollapsed ? 'space-y-1' : 'space-y-6'}`}
      >
        {groups.map((group) => {
          const groupCollapsed = !isCollapsed && collapsedGroups.has(group.title);
          return (
            <div key={group.title}>
              {!isCollapsed && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.title)}
                  className="flex items-center justify-between w-full px-4 mb-2 text-sm font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-300 transition-colors"
                >
                  <span>{group.title}</span>
                  <ChevronDownIcon
                    className={`w-3 h-3 transition-transform ${groupCollapsed ? '-rotate-90' : ''}`}
                  />
                </button>
              )}
              {!groupCollapsed && (
                <ul className="space-y-1">
                  {group.items.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        className={navLinkClass(isItemActive(item, pathname), isCollapsed)}
                        title={isCollapsed ? item.label : undefined}
                      >
                        {item.icon}
                        {!isCollapsed && <span>{item.label}</span>}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-neutral-800 p-3">
        <SidebarUserMenu isCollapsed={isCollapsed} auth={auth} />
      </div>
    </aside>
  );
}
